import React from 'react';
import {observer} from 'mobx-react';
import s from '../s';
import ATCButton from '../components/atc/atc-button';
import QtyComponent from '../components/qty';
import {getPriceDisplay} from '../pricing-request/price-display';
import {getGradYearAtcError} from '../components/grad-year/grad-year-util';
import {Portal} from '../../util';
import {redesign, type SizeQuestionId} from './redesign-store';
import {questionsLeft, shipLine, shipState} from './copy';
import {fastShipItem, fastShipTagsDecide} from './fast-ship';
import {CartIcon, TruckIcon} from './icons';

declare const modalOpen: (id: string) => void;

/** Scrolls a step into view and puts the keyboard on its first field or button. */
export const scrollToStep = (id: SizeQuestionId | 'school'): void => {
    const step = document.getElementById(`pp3-step-${id}`);
    if (!step) return;
    step.scrollIntoView({behavior: 'smooth', block: 'center'});
    const field = step.querySelector<HTMLElement>('input, select, button');
    field?.focus({preventScroll: true});
};

/**
 * The redesign's check before the control's Add To Cart checks (ATCButton beforeAdd): with a size question open nothing is added,
 * the page scrolls to the first open question and every open one turns red. A missing required graduation year marks the school
 * step and lets the control's own check report it under the button.
 */
export const beforeRedesignAddToCart = (): boolean => {
    const open = redesign.open;
    if (open.length > 0) {
        redesign.markTried();
        scrollToStep(open[0]);
        return false;
    }
    const gradYearError = getGradYearAtcError(s.ui.show_grad_year, s.ui.grad_year_input || '', s.ps.grad_year || '', s.prod.YearOfGraduation_Required);
    if (gradYearError) {
        redesign.setGradYearMissed(true);
        scrollToStep(redesign.yearShown ? 'year' : 'school');
    }
    return true;
};

/** The current ship state from the store's observable Fast Ship flag. */
export const currentShipState = () => shipState(fastShipItem(), s.ui.fast_ship_now, fastShipTagsDecide());

/**
 * The Add To Cart row: the cart mark on the rail, the questions still open, the total, quantity and the control's Add To Cart. On a
 * phone the pinned bar carries the total and Add To Cart (the 390px artboards), so the row shows "Your frame: ..." beside the mark.
 */
const AtcRow: React.FC<{summary: string}> = observer(({summary}) => {
    const open = redesign.open;
    const price = getPriceDisplay(s.ui);
    const qty = (s.ps.wholesale && s.prod && s.si && s.si.StoreTypeID !== 5) || (s.si.VolumeDiscounts && s.si.VolumeDiscounts.length > 0);

    return (
        <div
            className="pp3-step pp3-atc-step"
            role="group"
            aria-label="Add to cart"
        >
            <span
                className="pp3-num pp3-num-cart"
                aria-hidden="true"
            >
                <CartIcon />
            </span>
            <div className="pp3-step-body">
                {summary && (
                    <p className="pp3-yourframe">
                        <strong>Your frame: </strong>
                        {summary}
                    </p>
                )}
                <div className="pp3-atc-row">
                    {open.length > 0 && <p className={`pp3-left${redesign.tried ? ' missed' : ''}`}>{questionsLeft(open.length)}</p>}
                    {s.ui.show_pricing && (
                        <p
                            className="pp3-total"
                            aria-busy={s.ui.pricing_loading}
                        >
                            <span>Your total</span>
                            <strong>{price.current}</strong>
                        </p>
                    )}
                    {qty && (
                        <div className="pp3-qty">
                            <QtyComponent />
                        </div>
                    )}
                    {s.ui.show_atc && (
                        <div className="pp3-atc">
                            <ATCButton
                                additionalClass="pp3-atc-btn"
                                beforeAdd={beforeRedesignAddToCart}
                            />
                        </div>
                    )}
                </div>
                <div
                    aria-live="assertive"
                    aria-atomic="true"
                    className="sr-only"
                >
                    {s.ui.atc_busy ? 'Adding to cart, please wait...' : ''}
                </div>
                {s.ui.show_atc && s.ps.atc_error && s.ps.atc_error !== '' && (
                    <p
                        className="pp3-atc-error"
                        role="alert"
                    >
                        {s.ps.atc_error}
                    </p>
                )}
            </div>
        </div>
    );
});

/** C1-C3: "Fast Ship: ships in 2-3 days.", the standard ship time with a hint once a tagged pick drops Fast Ship, or without one. */
export const ShipLine: React.FC = observer(() => {
    const state = currentShipState();
    const line = shipLine(state);
    return (
        <p
            className={`pp3-ship pp3-ship-${state}`}
            aria-live="polite"
        >
            <TruckIcon />
            <span>
                <strong>{line.text}</strong>
                {line.hint && <span className="pp3-muted"> {line.hint}</span>}
            </span>
        </p>
    );
});

/** "Easy, 120-day return policy" (the return policy modal), "Handcrafted in the USA", "Level-Lock hanging system included". */
export const TrustLine: React.FC = observer(() => (
    <div className="pp3-trust">
        {s.isRetail && (
            <button
                type="button"
                className="pp3-link"
                onClick={() => modalOpen('modal-return-policy')}
                aria-haspopup="dialog"
                aria-controls="modal-return-policy"
            >
                Easy, {Portal && Portal.PortalID < 3 ? '120' : '30'}-day return policy
            </button>
        )}
        <span>Handcrafted in the USA</span>
        {s.prod.LevelLock && !s.prod.HideLevelLock && <span>Level-Lock hanging system included</span>}
    </div>
));

export default AtcRow;
